import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getInventoryCogsReport,
  getInventoryValuationReport,
} from '../../utils/inventoryApi';
import type { InventoryCogsReport, InventoryValuationReport } from '../../types/inventory';

function formatMoney(value: string | number) {
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

function formatInt(n: number) {
  return new Intl.NumberFormat().format(n);
}

export const InventoryReports: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [valuation, setValuation] = useState<InventoryValuationReport | null>(null);
  const [cogs, setCogs] = useState<InventoryCogsReport | null>(null);

  useEffect(() => {
    const pid = practiceId;
    if (!pid) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [val, c] = await Promise.all([
          getInventoryValuationReport(pid),
          getInventoryCogsReport(pid),
        ]);
        if (cancelled) return;
        setValuation(val);
        setCogs(c);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const valuationRows = useMemo(() => valuation?.rows ?? [], [valuation]);
  const cogsRows = useMemo(() => cogs?.rows ?? [], [cogs]);

  if (!practiceId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-gray-600">No practice selected.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-gray-600">Loading reports…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Valuation */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-gray-900">Valuation</div>
            <div className="text-sm text-gray-600">
              Unknown-cost lots are excluded from valuation totals.
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Total</div>
            <div className="text-lg font-semibold text-gray-900">
              {valuation ? formatMoney(valuation.total_valuation) : '—'}
            </div>
            <div className="text-xs text-gray-500">
              Unknown qty: {valuation ? formatInt(valuation.total_unknown_cost_quantity) : '—'}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  On hand
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valuation
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unknown qty
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {valuationRows.map((r) => (
                <tr key={`${r.catalog_type}:${r.catalog_item_id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{r.catalog_type}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatInt(r.on_hand)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatMoney(r.valuation)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatInt(r.unknown_cost_quantity)}</td>
                </tr>
              ))}
              {valuationRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-sm text-gray-600 text-center">
                    No valuation data yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* COGS */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-gray-900">COGS</div>
            <div className="text-sm text-gray-600">
              {cogs ? `Range: ${cogs.start_date} → ${cogs.end_date}` : 'Range: —'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Total</div>
            <div className="text-lg font-semibold text-gray-900">
              {cogs ? formatMoney(cogs.total_cogs) : '—'}
            </div>
            <div className="text-xs text-gray-500">
              Unknown qty: {cogs ? formatInt(cogs.total_unknown_cost_quantity) : '—'}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qty consumed
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  COGS
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unknown qty
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {cogsRows.map((r) => (
                <tr key={`${r.catalog_type}:${r.catalog_item_id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{r.catalog_type}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatInt(r.quantity_consumed)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatMoney(r.cogs)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatInt(r.unknown_cost_quantity)}</td>
                </tr>
              ))}
              {cogsRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-sm text-gray-600 text-center">
                    No consumption data yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


